import { createContext, useContext, type ReactNode } from "react"
import { useChains } from "wagmi"
import { useAuth } from "@/contexts/auth-context"
import { useDepositVaults, useStableTokens } from "@/hooks/use-gas-account"
import { useTransactionStats } from "@/hooks/use-transactions"
import type { DepositVaultsMap, StableToken } from "@/services/gas-account"
import type { GasAccountBalanceData } from "@/services/gas-account"
import type { SponsoredAmountPerDate } from "@/services/transactions"

export interface AppGlobalDataValue {
  /** Networks from wagmi (useChains). */
  networks: ReturnType<typeof useChains>
  /** Stable tokens from API, fetched once and shared across the app. */
  stableTokens: StableToken[]
  stableTokensLoading: boolean
  /** Deposit vault addresses from API, fetched once and shared across the app. */
  depositVaults: DepositVaultsMap
  depositVaultsLoading: boolean
  /** Gas account balance from GET /v1/transactions/stats. */
  gasAccountBalance: GasAccountBalanceData | undefined
  gasAccountBalanceLoading: boolean
  gasAccountBalanceFetching: boolean
  gasAccountBalanceError: unknown
  refetchGasAccountBalance: () => void
  /** Last 30 days sponsored amount USD per date (for chart). From same stats API. */
  transactionStatsDaily: SponsoredAmountPerDate[]
}

const AppGlobalDataContext = createContext<AppGlobalDataValue | null>(null)

export function AppGlobalDataProvider({ children }: { children: ReactNode }) {
  const { isVerified } = useAuth()

  const networks = useChains()
  const { data: stableTokensData, isLoading: stableTokensLoading } =
    useStableTokens(isVerified)
  const stableTokens = stableTokensData?.data ?? []
  const { data: depositVaultsData, isLoading: depositVaultsLoading } =
    useDepositVaults(isVerified)
  const depositVaults = depositVaultsData?.data ?? {}

  const transactionStatsQuery = useTransactionStats(isVerified)
  const gasAccountBalance =
    transactionStatsQuery.data?.data?.gas_account_balance ?? undefined
  const gasAccountBalanceLoading = transactionStatsQuery.isLoading
  const gasAccountBalanceFetching = transactionStatsQuery.isFetching
  const gasAccountBalanceError = transactionStatsQuery.error
  const refetchGasAccountBalance = () => transactionStatsQuery.refetch()
  const transactionStatsDaily =
    transactionStatsQuery.data?.data?.sponsored_amount_usd_per_date ?? []

  const value: AppGlobalDataValue = {
    networks,
    stableTokens,
    stableTokensLoading,
    depositVaults,
    depositVaultsLoading,
    gasAccountBalance,
    gasAccountBalanceLoading,
    gasAccountBalanceFetching,
    gasAccountBalanceError,
    refetchGasAccountBalance,
    transactionStatsDaily,
  }

  return (
    <AppGlobalDataContext.Provider value={value}>
      {children}
    </AppGlobalDataContext.Provider>
  )
}

export function useAppGlobalData(): AppGlobalDataValue {
  const ctx = useContext(AppGlobalDataContext)
  if (ctx == null) {
    throw new Error(
      "useAppGlobalData must be used within AppGlobalDataProvider"
    )
  }
  return ctx
}

/** Resolve token by address and chainId from stable tokens list. */
export function getTokenByAddress(
  stableTokens: StableToken[],
  tokenAddress: string | undefined,
  chainId: number | undefined
): StableToken | null {
  if (!tokenAddress || chainId == null) return null
  const token = stableTokens.find(
    (t) =>
      t.address.toLowerCase() === tokenAddress.toLowerCase() &&
      t.chainId === chainId
  )
  return token ?? null
}

/** Resolve token symbol by address and chainId from stable tokens list. */
export function getTokenSymbol(
  stableTokens: StableToken[],
  tokenAddress: string | undefined,
  chainId: number | undefined
): string | null {
  const token = getTokenByAddress(stableTokens, tokenAddress, chainId)
  return token?.symbol ?? null
}
